<script setup>
/**
 * One real call to the owner's own system, on demand.
 *
 * THE DISTINCTION THIS PANEL IS BUILT ON: **a test that ran and failed is an answer.** If
 * the other system refuses, times out or replies with a 500, the request worked perfectly
 * and what came back is exactly what the owner opened this panel to see. Only a thrown
 * error means the test did not happen, and it gets its own sentence. Collapsing the two —
 * which is what a bare "something went wrong" does — sends somebody to check their internet
 * connection when their own server is down, and vice versa.
 *
 * IT ONLY EVER TRIES WHAT IS SAVED. The dry run is executed by the sidecar against the
 * STORED row, so a draft with unsaved edits would be tested as it was before them — which
 * is the most confusing possible outcome for a panel whose whole job is "did my change
 * work". So the button is replaced by one sentence until the page is clean, rather than
 * running against something other than what is on screen.
 *
 * A CONNECTION THAT CHANGES SOMETHING IS SHOWN BEFORE IT IS SENT (P-3.4). Its first button
 * asks the sidecar what the call WOULD send and nothing reaches her system; only a second
 * button, behind a box she has to tick, sends it for real. A lookup keeps the one button.
 *
 * The values are the owner's own text and go through `WasloInput`, so an Arabic area name
 * reads right (R7). They are sent as raw strings: the sidecar coerces them to the declared
 * kind — `true`/`1`/`yes` for a yes-or-no, and so on — and duplicating that here would only
 * let the two disagree about what "0" means.
 */
import { computed, ref, watch } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

import WasloInput from './WasloInput.vue';
import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  /** The declared details of the SAVED connection, not of the draft. */
  params: { type: Array, default: () => [] },
  /** True while the page has edits that are not saved yet. */
  isDirty: { type: Boolean, default: false },
  /**
   * The details Waslo fills in that the SAVED connection sends (`contactDetails`, P-3.1). A test
   * has no customer behind it, so each one gets a "Sample customer" box (P-3.7); left empty, the
   * sidecar sends nothing and names the box.
   */
  contactDetails: { type: Array, default: () => [] },
  /** A sign-in with nothing stored behind it (P-3.8): nothing is tried until one is saved. */
  needsSignIn: { type: Boolean, default: false },
  isRunning: { type: Boolean, default: false },
  /**
   * `{ ok, status, data }` / `{ ok, status, error, detail? }`, or null before the first
   * run. `detail` is a bounded excerpt of the other system's own error body, when it
   * sent one that could be read.
   */
  result: { type: Object, default: null },
  /** Set when the call itself could not be made. */
  errorMessage: { type: String, default: '' },
  /** `{ <detailName>: <one sentence> }` from a refused run. */
  fieldErrors: { type: Object, default: () => ({}) },
  /** `lookup` or `change` (P-3.4). A change is shown before it is ever sent. */
  purpose: { type: String, default: 'lookup' },
});

const emit = defineEmits(['run']);

const { t } = useWasloI18n();

const key = name => `WASLO.SETTINGS.CONNECTIONS.EDITOR.TRY.${name}`;

const values = ref({});

// Keep a box for every declared detail and forget the ones that are gone, so renaming a
// detail does not quietly keep posting the old one's value.
watch(
  () => props.params,
  next => {
    const kept = {};
    (next ?? []).forEach(param => {
      kept[param.name] = values.value[param.name] ?? '';
    });
    values.value = kept;
  },
  { immediate: true, deep: true }
);

const setValue = (name, value) => {
  values.value = { ...values.value, [name]: value };
};

// ── the sample customer (P-3.7) ─────────────────────────────────────────────────────
const sample = ref({});

watch(
  () => props.contactDetails,
  next => {
    const kept = {};
    (next ?? []).forEach(name => {
      kept[name] = sample.value[name] ?? '';
    });
    sample.value = kept;
  },
  { immediate: true, deep: true }
);

const setSample = (name, value) => {
  sample.value = { ...sample.value, [name]: value };
};

/** Only the boxes she filled in: an empty one is "this test has none", which the server names. */
const typedSample = () =>
  Object.fromEntries(
    Object.entries(sample.value)
      .map(([name, value]) => [name, String(value).trim()])
      .filter(([, value]) => value !== '')
  );

/** `contact.phone_number` → `SAMPLE.CONTACT_PHONE_NUMBER`. */
const sampleLabel = name =>
  t(key(`SAMPLE.${name.replace('.', '_').toUpperCase()}`));

/** Ticked by the owner before "Send it for real" does anything (P-3.4). */
const confirmSend = ref(false);

/**
 * The values the shown preview was made from. "Send it for real" sends exactly these, and
 * only while the boxes still hold them: the owner agreed to what she was shown, so a value
 * typed after the preview asks for a fresh one instead of sending something she never saw.
 * The same holds for the connection itself: an edit to it clears this and the tick (the
 * watch below), and the page drops the preview outright once that edit is saved.
 */
const previewed = ref(null);

/** The values AND the sample customer, as one comparable string. */
const snapshot = () => JSON.stringify([values.value, typedSample()]);

// An edit to the connection itself, saved or not, is not what she agreed to. The tick and
// the values it covered go the moment the page has an edit on it, so "Send it for real"
// asks for a fresh preview even if the edit is put back by hand without saving.
watch(
  () => props.isDirty,
  dirty => {
    if (!dirty) return;
    confirmSend.value = false;
    previewed.value = null;
  }
);

const run = () => {
  if (props.isRunning || props.isDirty || props.needsSignIn) return;
  confirmSend.value = false;
  previewed.value = snapshot();
  emit('run', { ...values.value }, { send: false, sample: typedSample() });
};

const preview = computed(() => props.result?.preview ?? null);

// A value or a sample typed after the preview asks for a fresh one: she agreed to what she saw.
const previewIsCurrent = computed(
  () => previewed.value !== null && previewed.value === snapshot()
);

const canSend = computed(
  () =>
    !props.isRunning &&
    !props.isDirty &&
    confirmSend.value &&
    previewIsCurrent.value
);

const sendForReal = () => {
  if (!canSend.value) return;
  emit('run', { ...values.value }, { send: true, sample: typedSample() });
};

const previewBody = computed(() =>
  preview.value?.body == null ? '' : JSON.stringify(preview.value.body, null, 2)
);

/** What came back, as text. Capped: a remote is free to answer with a megabyte of HTML. */
const MAX_SHOWN = 4000;

const shownResult = computed(() => {
  if (!props.result || !props.result.ok) return '';
  const text = JSON.stringify(props.result.data ?? null, null, 2);
  return text.length > MAX_SHOWN ? `${text.slice(0, MAX_SHOWN)}…` : text;
});

/**
 * What the other system's error body said, or '' when there is nothing to show. Only a
 * failed run carries it; a blank or non-string one is treated as absent rather than
 * rendered as an empty box under a label.
 */
const shownDetail = computed(() => {
  if (!props.result || props.result.ok) return '';
  const { detail } = props.result;
  return typeof detail === 'string' ? detail.trim() : '';
});

const headline = computed(() => {
  if (preview.value) return '';
  if (!props.result) return '';
  if (props.result.ok) {
    return t(key('OK_STATUS'), { status: props.result.status });
  }
  return props.result.status === null
    ? t(key('NO_STATUS'))
    : t(key('FAILED_STATUS'), { status: props.result.status });
});
</script>

<template>
  <div class="flex flex-col gap-4" data-waslo-connection-try>
    <div v-if="params.length" class="flex flex-col gap-3">
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('VALUES')) }}
      </p>
      <WasloInput
        v-for="param in params"
        :key="param.name"
        :model-value="values[param.name] ?? ''"
        :label="param.name"
        :hint="param.description"
        :message="fieldErrors[param.name] || ''"
        :message-type="fieldErrors[param.name] ? 'error' : 'info'"
        :data-waslo-try-value="param.name"
        @update:model-value="value => setValue(param.name, value)"
      />
    </div>

    <!-- P-3.7: the customer a test does not have. One box per detail we fill in. -->
    <div
      v-if="contactDetails.length"
      class="flex flex-col gap-3"
      data-waslo-try-sample
    >
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('SAMPLE_HEADING')) }}
      </p>
      <p class="mb-0 text-sm text-n-slate-11">{{ t(key('SAMPLE_HINT')) }}</p>
      <WasloInput
        v-for="name in contactDetails"
        :key="name"
        :model-value="sample[name] ?? ''"
        :label="sampleLabel(name)"
        :message="fieldErrors[name] || ''"
        :message-type="fieldErrors[name] ? 'error' : 'info'"
        :data-waslo-try-sample-value="name"
        @update:model-value="value => setSample(name, value)"
      />
    </div>

    <div class="flex flex-wrap items-center gap-3">
      <p
        v-if="!isDirty && needsSignIn"
        class="mb-0 text-sm text-n-slate-11"
        data-waslo-try-needs-sign-in
      >
        {{ t(key('NEEDS_SIGN_IN')) }}
      </p>
      <Button
        v-else-if="!isDirty"
        :label="
          isRunning
            ? t(key('RUNNING'))
            : purpose === 'change'
              ? t(key('PREVIEW_ACTION'))
              : t(key('ACTION'))
        "
        icon="i-lucide-play"
        variant="faded"
        color="slate"
        size="sm"
        :is-loading="isRunning"
        :disabled="isRunning"
        data-waslo-try-run
        @click="run"
      />
      <p v-else class="mb-0 text-sm text-n-slate-11" data-waslo-try-needs-save>
        {{ t(key('NEEDS_SAVE')) }}
      </p>
    </div>

    <!-- The call could not be made at all. Different sentence, different meaning. -->
    <p
      v-if="errorMessage"
      class="mb-0 text-sm text-n-ruby-9"
      data-waslo-try-unavailable
    >
      {{ errorMessage }}
    </p>

    <!-- P-3.4: a change is shown before anything reaches the owner's system, and sent
         only once she has ticked the box that says it really will be. -->
    <div
      v-else-if="preview"
      class="flex flex-col gap-3 p-4 outline outline-1 -outline-offset-1 rounded-xl bg-n-solid-1 outline-n-weak"
      data-waslo-try-preview
    >
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('PREVIEW_TITLE')) }}
      </p>
      <div class="flex flex-col gap-1">
        <p class="mb-0 text-xs text-n-slate-10">
          {{ t(key('PREVIEW_ADDRESS')) }}
        </p>
        <div
          class="px-3 py-2 overflow-x-auto font-mono text-xs whitespace-pre rounded-lg bg-n-alpha-1 text-n-slate-12"
          dir="ltr"
        >
          {{ preview.method }} {{ preview.url }}
        </div>
      </div>
      <div v-if="preview.headerNames.length" class="flex flex-col gap-1">
        <p class="mb-0 text-xs text-n-slate-10">
          {{ t(key('PREVIEW_HEADERS')) }}
        </p>
        <p class="mb-0 font-mono text-xs text-n-slate-12" dir="ltr">
          {{ preview.headerNames.join(', ') }}
        </p>
      </div>
      <div v-if="previewBody" class="flex flex-col gap-1">
        <p class="mb-0 text-xs text-n-slate-10">
          {{ t(key('PREVIEW_BODY')) }}
        </p>
        <div
          class="px-3 py-2 overflow-x-auto font-mono text-xs whitespace-pre rounded-lg bg-n-alpha-1 text-n-slate-12"
          dir="ltr"
        >
          {{ previewBody }}
        </div>
      </div>
      <label
        class="flex items-start gap-3 text-sm cursor-pointer text-n-slate-12"
      >
        <span class="mt-0.5 shrink-0">
          <Checkbox v-model="confirmSend" data-waslo-try-send-confirm />
        </span>
        <span class="max-w-[62ch]">{{ t(key('SEND_CONFIRM')) }}</span>
      </label>
      <Button
        :label="t(key('SEND_ACTION'))"
        icon="i-lucide-send"
        variant="faded"
        color="slate"
        size="sm"
        :is-loading="isRunning"
        :disabled="!canSend"
        data-waslo-try-send
        @click="sendForReal"
      />
    </div>

    <div
      v-else-if="result && !preview"
      class="flex flex-col gap-3 p-4 outline outline-1 -outline-offset-1 rounded-xl bg-n-solid-1"
      :class="result.ok ? 'outline-n-weak' : 'outline-n-amber-8'"
      :data-waslo-try-result="result.ok ? 'ok' : 'failed'"
    >
      <p
        class="flex items-center gap-2 mb-0 text-sm font-medium"
        :class="result.ok ? 'text-n-blue-11' : 'text-n-amber-11'"
      >
        <span
          class="shrink-0 size-4"
          :class="
            result.ok ? 'i-lucide-circle-check' : 'i-lucide-triangle-alert'
          "
          aria-hidden="true"
        />
        {{ headline }}
      </p>

      <div class="flex flex-col gap-1">
        <p class="mb-0 text-xs text-n-slate-10">
          {{ result.ok ? t(key('RESULT')) : t(key('ERROR_TITLE')) }}
        </p>
        <!--
          `dir="ltr"` and not `dir="auto"`: this is a machine's answer, and JSON reads left
          to right whatever language the values inside it are in. `auto` would flip the
          whole block on the first Arabic string in it and put the braces on the wrong side.
        -->
        <!--
          A `div` with `whitespace-pre`, not a `<pre>`: inside a real `<pre>` every
          character between the tags is content, so the formatter's own line breaks become
          a leading and a trailing blank line in what the owner reads. Same rendering,
          nothing whitespace-sensitive to protect.
        -->
        <div
          class="px-3 py-2 overflow-x-auto font-mono text-xs whitespace-pre rounded-lg bg-n-alpha-1 text-n-slate-12"
          dir="ltr"
          data-waslo-try-body
        >
          {{ result.ok ? shownResult : result.error }}
        </div>
      </div>

      <!-- The remote's own words, under our one-line reading of them. Wrapped and capped
           in height: the sidecar bounds the excerpt, but a 500-character JSON error on
           one line would still push the page sideways. -->
      <div v-if="shownDetail" class="flex flex-col gap-1" data-waslo-try-detail>
        <p class="mb-0 text-xs text-n-slate-10">
          {{ t(key('DETAIL_LABEL')) }}
        </p>
        <div
          class="px-3 py-2 overflow-y-auto font-mono text-xs break-words whitespace-pre-wrap rounded-lg max-h-40 bg-n-alpha-1 text-n-slate-12"
          dir="ltr"
        >
          {{ shownDetail }}
        </div>
      </div>
    </div>
  </div>
</template>
